import React, { useEffect } from "react";
import { X, Menu, Settings, LogOut } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

/**
 * Mobile hamburger drawer:
 * - Shows only on <lg screens (masaüstünde Sidebar zaten görünür).
 * - Renders the desktop Sidebar inside a slide-in panel.
 */
const MobileDrawer = ({ open, onClose, children }) => {
  const navigate = useNavigate();
  const { logout, user } = useAuth();

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && onClose();
    const onPop = () => onClose();
    window.addEventListener("keydown", onKey);
    window.history.pushState({ mobileDrawer: true }, "");
    window.addEventListener("popstate", onPop);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("popstate", onPop);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);

  if (!open) return null;

  const handleLogout = async () => {
    await logout();
    onClose();
    toast.success("Güvenli çıkış yapıldı");
  };

  return (
    <>
      <div
        className="lg:hidden fixed inset-0 bg-black/40 z-[55]"
        onClick={onClose}
        data-testid="mobile-drawer-backdrop"
      />
      <aside
        className="lg:hidden fixed inset-y-0 left-0 w-[85%] max-w-[320px] bg-white dark:bg-[#18181B] z-[56] flex flex-col shadow-xl"
        style={{ paddingTop: "env(safe-area-inset-top)", paddingBottom: "env(safe-area-inset-bottom)" }}
        data-testid="mobile-drawer"
        role="dialog"
        aria-modal="true"
      >
        <div className="flex items-center justify-between p-3 border-b border-[#E4E4E7] dark:border-[#27272A]">
          <span className="text-sm font-semibold">Menü</span>
          <button
            onClick={onClose}
            className="p-2 -mr-2 text-[#52525B] dark:text-[#A1A1AA] active:scale-95"
            aria-label="Menüyü kapat"
            data-testid="mobile-drawer-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
          {children}
        </div>

        <div className="border-t border-[#E4E4E7] dark:border-[#27272A] p-3 space-y-1">
          <button
            data-testid="mobile-drawer-settings"
            onClick={() => { onClose(); navigate("/settings"); }}
            className="w-full flex items-center gap-2 px-3 py-2 rounded text-sm text-[#52525B] dark:text-[#A1A1AA] hover:bg-[#F9FAFB] dark:hover:bg-[#27272A] active:scale-95 transition"
          >
            <Settings className="w-4 h-4" />
            Hesap ve Güvenlik
          </button>
          <button
            data-testid="mobile-drawer-logout"
            onClick={handleLogout}
            className="w-full flex items-center gap-2 px-3 py-2 rounded text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 active:scale-95 transition"
          >
            <LogOut className="w-4 h-4" />
            Güvenli Çıkış
          </button>
        </div>
      </aside>
    </>
  );
};

export const MobileHamburgerButton = ({ onClick }) => (
  <button
    onClick={onClick}
    className="lg:hidden fixed top-3 left-3 z-40 w-10 h-10 rounded-full bg-white dark:bg-[#18181B] border border-[#E4E4E7] dark:border-[#27272A] shadow flex items-center justify-center active:scale-95 transition"
    style={{ marginTop: "env(safe-area-inset-top)" }}
    aria-label="Menüyü aç"
    data-testid="mobile-hamburger-button"
  >
    <Menu className="w-5 h-5 text-[#0A0A0A] dark:text-[#FAFAFA]" />
  </button>
);

export default MobileDrawer;
